import { useState } from 'react';
import { CheckIcon } from 'lucide-react';
import { DropdownMenuItem, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { useTenantEntity } from './tenant-entity-provider';
import { allowTenantEntityScopeChange } from './tenant-entity-switcher';

/** Shared by header and account menu; the provider remains the only switching owner. */
export function ScopeChoices({ kind, close }: { kind: 'tenant' | 'entity'; close: () => void }) {
  const access = useTenantEntity();
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const tenant = access.tenants.find(t => t.id === access.selected?.tenantId);
  const choices = kind === 'tenant' ? access.tenants.map(t => ({
    id: t.id, name: t.name, tenantId: t.id,
    entityId: t.id === access.selected?.tenantId ? access.selected.entityId : t.entities.find(e => e.status === 'active')?.id,
    active: t.status === 'active'
  })) : (tenant?.entities ?? []).map(e => ({ id: e.id, name: e.name, tenantId: tenant!.id, entityId: e.id, active: e.status === 'active' && tenant?.status === 'active' }));
  const disabled = busy || access.contextsLoading || access.status === 'switching' || access.status === 'loading';
  const total = Math.max(access.contextTotal, access.accessSummary?.hierarchyTotal ?? 0);
  const filtered = choices.filter(c => c.name.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase()));
  const select = async (tenantId: string, entityId: string) => {
    if (!allowTenantEntityScopeChange()) return;
    setBusy(true); setError(null);
    try { await access.selectContext(tenantId, entityId); close(); }
    catch (e) { setError(e instanceof Error ? e.message : 'Could not switch entity.'); }
    finally { setBusy(false); }
  };
  return <>
    <div className="p-1" onKeyDown={e => e.stopPropagation()}>
      <Input autoFocus aria-label={`Search ${kind === 'tenant' ? 'tenants' : 'entities'}`} placeholder="Search this page…" value={search} onChange={e => setSearch(e.target.value)} onPointerDown={e => e.stopPropagation()} />
    </div>
    <div className="max-h-72 overflow-y-auto">
      {filtered.map(c => <DropdownMenuItem key={c.id} disabled={disabled || !c.active || !c.entityId}
        onSelect={e => { e.preventDefault(); if (c.entityId) void select(c.tenantId, c.entityId); }}>
        <span className="truncate">{c.name}</span>
        {c.id === (kind === 'tenant' ? access.selected?.tenantId : access.selected?.entityId) && <CheckIcon className="ml-auto" aria-label={`Current ${kind}`} />}
      </DropdownMenuItem>)}
      {!filtered.length && <p className="p-3 text-sm text-muted-foreground">No matching {kind === 'tenant' ? 'tenants' : 'entities'} on this page.</p>}
    </div>
    {(access.contextOffset > 0 || total > 100) && <>
      <DropdownMenuSeparator />
      <DropdownMenuItem disabled={disabled || access.contextOffset === 0} onSelect={e => { e.preventDefault(); access.setContextOffset(Math.max(0, access.contextOffset - 100)); }}>Previous tenants &amp; entities</DropdownMenuItem>
      <DropdownMenuItem disabled={disabled || access.contextOffset + 100 >= total} onSelect={e => { e.preventDefault(); access.setContextOffset(access.contextOffset + 100); }}>Next tenants &amp; entities</DropdownMenuItem>
    </>}
    {error && <p role="alert" className="p-2 text-sm">{error}</p>}
  </>;
}
